import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import RADApplication from '@/Management/RADApplication';
import * as DownloadHelper from '@/Utility/DownloadHelper';
import type ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import {
    IEmbeddedMediumImageLabelViewModelConstructorParameter
} from "#/DefaultViewExtensionInterfaces";

export default class EmbeddedMediumImageLabelViewModel extends ControlViewModel {

    private readonly image: ImageViewModelT;

    public readonly thumbWidth: number;
    public readonly thumbHeight: number;

    private readonly pdoId: KnockoutObservable<string>;
    private readonly lockGroupId: KnockoutObservable<string>;
    private readonly attributeName: KnockoutObservable<string>;
    private readonly showImage: KnockoutObservable<boolean>;
    private readonly alternativeText: KnockoutObservable<string>;

    private readonly url: KnockoutComputed<string>;

    private previewObjectUrl: string | null = null;

    public constructor(protected params: IEmbeddedMediumImageLabelViewModelConstructorParameter) {
        super(params);
        const ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');

        this.thumbWidth = this.paramOrDefault(params.thumbWidth, 150, (val: number, def: number) => val > 0 ? val : def);
        this.thumbHeight = this.paramOrDefault(params.thumbHeight, 150, (val: number, def: number) => val > 0 ? val : def);

        this.pdoId = this.observableOrDefault(params.pdoId, null);
        this.lockGroupId = this.observableOrDefault(params.lockGroupId, null);
        this.attributeName = this.observableOrDefault(params.attributeName, null);
        this.showImage = this.observableOrDefault(params.showImage, false);
        this.alternativeText = this.observableOrDefault(params.alternativeText, null);

        this.url = this.defineComputed(() => {
            const showImage = this.showImage();
            const pdoid = this.pdoId();
            const lockgroupid = this.lockGroupId();
            const attrName = this.attributeName();

            if (!showImage)
                return null;

            return this.createUrl(pdoid, lockgroupid, attrName);
        });

        this.image = new ImageViewModel({
            isConvertedSource: true,
            isVisible: this.showImage,
            source: '',
            alternativeText: this.alternativeText,
            width: this.thumbWidth,
            height: this.thumbHeight,
        });

        this.defineSubscription(this.url, url => this.updatePreviewImage(url))(this.url());
    }

    /**
     * Updates the image preview.
     */
    public updatePreview(): void {
        this.url.notifySubscribers(this.url());
    }

    /*
     * Erzeugt die URL für die Dateivorschau
     */
    private createUrl(pdoId: string, lockGroupId: string, attributeName: string): string {
        const mediumHandler = RADApplication.getInstance().radEnvironmentMediumHandler;

        const parameters = {
            PDOID: pdoId,
            LockGroupID: lockGroupId,
            AttributeName: attributeName,
            Preview: true.toString(),
            ThumbWidth: this.thumbWidth.toString(),
            ThumbHeight: this.thumbHeight.toString(),
            ScaleThumb: true.toString()
        };

        return mediumHandler + '?' + new URLSearchParams(parameters).toString();
    }

    private updatePreviewImage(sourceUrl: string | null) {
        if (!sourceUrl) {
            this.setPreviewObjectUrl(null);
            this.notifyPreviewImageSourceChange(null);
            return;
        }

        this.fetchPreviewImage(sourceUrl).then(([objectUrl, _]) => {
            if (this.isDisposed)
                return;

            this.setPreviewObjectUrl(objectUrl);
            this.notifyPreviewImageSourceChange(objectUrl);
        });
    }

    private notifyPreviewImageSourceChange(newSource: string | null) {
        const source = this.image?.source;
        if (source == null)
            return;

        if (newSource == source()) {
            source.notifySubscribers(newSource);
        } else {
            source(newSource);
        }
    }

    private async fetchPreviewImage(url: string | null): Promise<[string, string] | null> {
        const [blob, filename] = await DownloadHelper.fetchBlobFromApplicationAsync(url);
        const objectUrl = window.URL.createObjectURL(blob);
        return [objectUrl, filename]
    }

    private setPreviewObjectUrl(url: string | null) {
        if (this.previewObjectUrl != null) {
            window.URL.revokeObjectURL(this.previewObjectUrl);
        }

        this.previewObjectUrl = url;
    }

    public dispose() {
        this.setPreviewObjectUrl(null);
        super.dispose();
    }
}

ComponentManager.setViewModel('EmbeddedMediumImageLabelViewModel', EmbeddedMediumImageLabelViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');